import h from 'hyperscript';

import indicatorLocalContextSvg from '@disy/cadenza-icons/analysis-context-data-view.svg';
import indicatorAnalysisContextSvg from '@disy/cadenza-icons/analysis-context.svg';
import scaleDependentSvg from '@disy/cadenza-icons/map.svg';
import { requireNonNullable } from '@disy/lib/custom-error';
import { entries, groupBy } from '@disy/lib/type-util';
import { createIconButton } from '@disy/ui/button';
import type { Content } from '@disy/ui/content';
import { icon } from '@disy/ui/icon';
import type { MenuContentElement, MenuItemGroup } from '@disy/ui/menu';
import { MENU_ITEM_SEPARATOR, setupMenuButton } from '@disy/ui/menu';
import { getSetting } from '@disy/workbook.lib/settings';

import { hierarchyLevelDetails } from 'ui/attribute-label/attribute-formatting-utils';

import type { ObjecttypeWithAttributes } from 'cadenza/api-client/dto/objecttype-dto';
import { isModified } from 'cadenza/attribute-util';
import { isNumeric } from 'cadenza/repository/model/attribute-type.gen';
import { MeasureAggregation, getTargetAttributeType } from 'cadenza/repository/model/measure-aggregation.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import { TableFractionCalculation } from 'cadenza/repository/model/table-fraction-calculation.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { attributeTile } from 'cadenza/ui/attribute-tile/attribute-tile';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeGroup } from 'cadenza/workbook/api/attribute-group';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import { DataViewVisualizationKey, isViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import { TopNSortOrder } from 'cadenza/workbook/api/top-n-sort-order';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { AttributeModificationController } from 'cadenza/workbook/attribute-modification-dialog/attribute-modification-dialog';
import { editActiveAttribute } from 'cadenza/workbook/attribute-modification-dialog/edit-attribute';
import type { AttributeHighlightingRule } from 'cadenza/workbook/attribute-modification-dialog/presentation/attribute-highlighting/attribute-highlighting-types';
import {
  createHierarchyControlMenuActions,
  isHierarchyControlledByMapScale,
} from 'cadenza/workbook/conditions/hierarchy-context-menu-actions';
import type { DataView } from 'cadenza/workbook/data-view/data-view';
import { getDataViewAcceptedAttributeIds, getViewType } from 'cadenza/workbook/data-view/data-view-utils';
import type { AttributeBundleDefinitionDto } from 'cadenza/workbook/model/attribute-bundle-definition-dto';
import type { AttributeBundleName, ByBundle } from 'cadenza/workbook/model/attribute-bundle-name';
import { UNUSED_BUNDLE_NAME } from 'cadenza/workbook/model/attribute-bundle-name';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { areRequiredAttributesFulfilled } from 'cadenza/workbook/view-validation/view-validation';
import {
  canManageObjecttype,
  getAttributeById,
  getDataViewById,
  getViewByDataViewId,
  getViewById,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { analysisContextAccepts } from 'cadenza/workbook/workbook-panel/analysis-context/analysis-context-attribute-bundle-definition';
import { addAttributeToAnalysisContextAction } from 'cadenza/workbook/workbook-panel/databrowser/data-browser-attribute-context-actions';
import { AttributeBundleValidator } from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator';
import type { AttributeBundleValidatorMessages } from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator-messages';
import type { CompatibleBundleProvider } from 'cadenza/workbook/workbook-panel/settings/attributes/compatible-bundle-provider';
import { DropController } from 'cadenza/workbook/workbook-panel/settings/attributes/drop-controller';
import { createMoveAttributeInBundleSubMenu } from 'cadenza/workbook/workbook-panel/settings/attributes/drop-panel/actions/move-attribute-in-bundle';
import { UNUSED_BUNDLE_REFERENCE } from 'cadenza/workbook/workbook-panel/settings/attributes/drop-panel/unused-bundle-reference';
import {
  getColorClassificationInvalidMessage,
  isColorClassificationInvalid,
} from 'cadenza/workbook/workbook-panel/settings/map/advanced-data-layer-settings/advanced-color-classification-settings/advanced-color-classification-settings-utils';

import i18n from './data-view-bundle-controller.properties';

export type DataViewBundleControllerOptions = Partial<{
  filterMessages: AttributeBundleValidatorMessages;
  requiredAttributesMissingMessage: string;
  acceptedObjecttypeRolesErrorMessage: string;
  defaultMessage: string;
  dropPanelPlaceholderMessage: string;
  viewCoverAddActionMessage: string;
  compatibleBundleProvider: CompatibleBundleProvider;
  withDetails: boolean;
  customInfoMessage: string;
}>;

interface TopNInformation {
  sortOrder: string;
  limit: string;
  measureAggregation: string;
  measureName: string;
}

export type DataViewBundleControllers = ByBundle<DataViewBundleController>;

/**
 * Controller for attribute modifications on data view.
 *
 * @implements {AttributeModificationController}
 */
export class DataViewBundleController extends DropController implements AttributeModificationController {
  readonly #dataView: DataView;
  readonly #bundleName;
  readonly #requiredAttributesMissingMessage;
  #defaultMessage;
  readonly #dropPanelPlaceholderMessage;
  readonly #viewCoverAddActionMessage;
  readonly #withDetails;
  readonly #customInfoMessage;
  #locked = false;

  #validator;

  readonly #compatibleBundleProvider?: CompatibleBundleProvider;

  /**
   * Creates a proxying controller for attributes access of this DataView that allows to be filtered
   * to the specified categories and types. Will fall back to allow all categories and attribute types.
   *
   * @param dataView - The DataView this controller adapts to
   * @param bundleName - The name of the attribute bundle the controller is assigned to
   * @param options - The options to restrict the controller
   */
  constructor(
    dataView: DataView,
    bundleName: AttributeBundleName,
    {
      filterMessages = {},
      requiredAttributesMissingMessage,
      acceptedObjecttypeRolesErrorMessage,
      defaultMessage,
      dropPanelPlaceholderMessage,
      viewCoverAddActionMessage,
      compatibleBundleProvider,
      withDetails = true,
      customInfoMessage,
    }: DataViewBundleControllerOptions = {},
  ) {
    super();
    this.#dataView = dataView;
    this.#bundleName = bundleName;
    this.#compatibleBundleProvider = compatibleBundleProvider;
    this.#requiredAttributesMissingMessage = requiredAttributesMissingMessage;
    this.#defaultMessage = defaultMessage;
    this.#dropPanelPlaceholderMessage = dropPanelPlaceholderMessage;
    this.#viewCoverAddActionMessage = viewCoverAddActionMessage;
    this.#withDetails = withDetails;
    this.#customInfoMessage = customInfoMessage;
    this.#validator = new AttributeBundleValidator({
      dataViewId: dataView.dataViewId,
      attributesProvider: () => this.attributes,
      filterMessages,
      acceptedObjecttypeRolesErrorMessage,
    });
  }

  setDefaultMessage(message: string) {
    this.#defaultMessage = message;
  }

  override get compatibleAttributesGroups(): ObjecttypeWithAttributes[] | AttributeGroup[] {
    return isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-browser') ?
        this.#createFullCompatibleAttributesGroups()
      : this.#createAttributeGroupsFromUnusedAttributes();
  }

  /**
   * Provides the attribute groups from the data browser, combined with the unused attributes all
   * together in a special group.
   *
   * @return the attribute groups
   */
  #createFullCompatibleAttributesGroups(): ObjecttypeWithAttributes[] | AttributeGroup[] {
    const compatibleAttributesGroups = super.compatibleAttributesGroups;

    // In case this is called for an Unused bundle we don't want to add the same again
    if (this.#bundleName === UNUSED_BUNDLE_NAME) {
      return compatibleAttributesGroups;
    }

    const unusedBundleAttributeGroup = this.#getUnusedBundleAttributeGroup();

    // If no addition attributes are available in the Unused bundle, omit it
    if (Object.keys(unusedBundleAttributeGroup.attributes).length === 0) {
      return compatibleAttributesGroups;
    }

    // Return merged list of attribute groups
    return [unusedBundleAttributeGroup, ...compatibleAttributesGroups];
  }

  /**
   * Provides the attribute groups from the unused attributes.
   *
   * @return the attribute groups
   */
  #createAttributeGroupsFromUnusedAttributes(): AttributeGroup[] {
    const unusedAttributes = this.#dataView.getAttributes(UNUSED_BUNDLE_NAME);
    const acceptedUnusedAttributes = unusedAttributes.filter((attribute) => this.accepts(attribute).accepts);
    const attributesByObjecttypeId = groupBy(acceptedUnusedAttributes, (attribute) => attribute.objecttypeId!);
    return entries(attributesByObjecttypeId).map(([key, values]) => ({
      id: key,
      printName: values[0].objecttypePrintName,
      attributes: values,
    }));
  }

  /**
   * Returns an AttributeGroup containing all compatible attributes from the Unused bundle of the
   * DataView this controller is linked to.
   *
   * @name AttributeListController#compatibleAttributesGroups
   * @return An AttributeGroup reflecting the compatible attributes from the unused bundle
   */
  #getUnusedBundleAttributeGroup(): AttributeGroup {
    const attributes = this.#dataView
      .getAttributes(UNUSED_BUNDLE_NAME)
      .filter((attribute) => this.accepts(attribute).accepts);

    return {
      id: UNUSED_BUNDLE_NAME as unknown as ObjecttypeIdString,
      printName: UNUSED_BUNDLE_REFERENCE.title,
      attributes,
    };
  }

  #isJasperReportView() {
    return this.dataView.visualizationKey === DataViewVisualizationKey.REPORT.JASPER_REPORT;
  }

  /**
   * Gets bundle definitions from dataView and overwrites static definitions from {@link FilterMessages}
   *
   * @return Object with definitions which make attribute valid drop candidate.
   */
  override getBundleDefinition(): AttributeBundleDefinitionDto {
    return {
      ...super.getBundleDefinition(),
      ...this.#dataView.attributeBundleDefinitions[this.#bundleName],
    };
  }

  get attributes(): WorkbookAttributeDto[] {
    return this.dataView.getAttributes(this.#bundleName);
  }

  get attributeIds(): ActiveAttributeId[] {
    return this.attributes.map(({ id }) => id as ActiveAttributeId);
  }

  reorder(attributeIds: ActiveAttributeId[]) {
    this.dataView.reorderAttributes(this.#bundleName, attributeIds);
  }

  add(attributeId: AttributeId, index?: number) {
    this.dataView.addAttribute(attributeId, this.#bundleName, index);
  }

  remove(attributeId: ActiveAttributeId) {
    this.dataView.removeAttribute(attributeId);
  }

  replace(removedAttributeId: ActiveAttributeId, addedAttributeId: AttributeId, index: number) {
    this.dataView.replaceAttribute(removedAttributeId, addedAttributeId, this.#bundleName, index);
  }

  removeAll() {
    this.dataView.removeAttributes(this.attributes.map((attribute) => attribute.id as ActiveAttributeId));
  }

  override get acceptedAttributeIds() {
    return getDataViewAcceptedAttributeIds(this.#dataView.dataViewId);
  }

  createElement(attribute: WorkbookAttributeDto) {
    const withManagementLink = canManageObjecttype(attribute.objecttypeId);
    const attributeIsInvalid =
      this.bundleName === 'COLOR_CLASSIFICATION' &&
      isColorClassificationInvalid(getViewByDataViewId(getStore().getState(), this.dataView.dataViewId).id);

    const attributeItem = attributeTile(
      attribute,
      {
        noDetails: true, // hide the original details to only display the custom details
        noLinkToManagementCenter: !withManagementLink,
        removable: isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-view-content') ? 'SURFACE' : false,
        menu: () => this.getMenuItems(attribute.id as ActiveAttributeId),
        invalid: attributeIsInvalid ? getColorClassificationInvalidMessage() : false,
        hoverContext: true,
        locked: this.#locked,
      },
      this.#withDetails ? this.#createAttributeTileDetails(attribute) : [],
    );
    attributeItem.addEventListener('remove', () => {
      this.remove(attribute.id as ActiveAttributeId);
    });

    const mapSrs = getViewByDataViewId(getStore().getState(), this.#dataView.dataViewId).metadata.mapSrs;
    if (mapSrs && attribute.relatedGeometryType === 'RASTER' && attribute.srs && mapSrs !== attribute.srs) {
      attributeItem.append(createSrsDiffersAlert());
    }

    if (attribute.status === 'INCOMPATIBLE_AGGREGATION_FUNCTION') {
      attributeItem.errorContent = this.#getAttributeTileErrorProvider(
        attribute,
        i18n('error.message.incompatibleAggregation'),
      );
    } else if (attribute.status === 'INCOMPATIBLE_AGGREGATIONS_FOR_FRACTION_CALCULATION') {
      attributeItem.errorContent = this.#getAttributeTileErrorProvider(
        attribute,
        i18n('error.message.incompatibleAggregationsForFractionCalculation'),
      );
    } else if (attribute.status === 'INCOMPATIBLE_FRACTION_CALCULATION_FOR_TABLE_MODE') {
      attributeItem.errorContent = this.#getAttributeTileErrorProvider(
        attribute,
        i18n('error.message.incompatibleFractionCalculationForTableMode'),
      );
    }

    return attributeItem;
  }

  #createAttributeTileDetails(attribute: WorkbookAttributeDto) {
    const detailsElements: Content = [];
    const hierarchyControlActionsProvider = this.#createHierarchyControlMenuActions.bind(this);
    if (attribute.hierarchyType) {
      const hierarchyControlButton = createHierarchyContextControlMenuButton(
        attribute,
        hierarchyControlActionsProvider,
        isHierarchyControlledByMapScale(this.#dataView.dataViewId, attribute),
      );
      detailsElements.push(hierarchyControlButton, ...hierarchyLevelDetails(attribute));
    } else if (attribute.category === 'MEASURE' && attribute.measureAggregation) {
      detailsElements.push(MeasureAggregation.i18n(attribute.measureAggregation!));
    }
    const topNInformation = this.getTopNInformation(attribute);
    if (topNInformation) {
      detailsElements.push(createTopNLimitedContentForAttributeTile(topNInformation));
    }
    const visualizationKey = this.dataView.visualizationKey;
    if (
      visualizationKey &&
      isViewType(visualizationKey, ViewType.TABLE) &&
      attribute.category === 'MEASURE' &&
      attribute.fractionCalculation &&
      attribute.fractionCalculation !== 'NONE' &&
      isNumeric(getTargetAttributeType(attribute.type, attribute.measureAggregation!))
    ) {
      detailsElements.push(h('p', TableFractionCalculation.i18n(attribute.fractionCalculation!)));
    }
    return detailsElements;
  }

  /**
   * This function is used to generate information about currently active configuration for top n.
   * If settings for this attribute don't meet all the requirements for top n configuration to be active,
   * null value is returned.
   * In order for Top N to be applied, these requirements must be met:
   * 1. Attribute must be the last attribute in the bundle.
   * 2. Top n must be enabled.
   * 3. Attribute must have attribute modifications set for sortOrder, limit, and measure.
   * 4. Referenced measure must exist in the designer.
   *
   * @param attribute - The attribute used to get top N information that needs to be shown in the designer.
   * @return Top N information (if it needs to be shown, otherwise null)
   */
  getTopNInformation(attribute: WorkbookAttributeDto): TopNInformation | null {
    const topNEnabled = getSetting<boolean>(attribute, AttributeModificationKey.TOP_N_ROWS_ENABLED);
    const limit = getSetting<number>(attribute, AttributeModificationKey.TOP_N_ROWS_MAX_ROWS_PER_GROUP);
    const measureId = getSetting<string>(attribute, AttributeModificationKey.TOP_N_MEASURE);
    const sortOrder = getSetting<string>(attribute, AttributeModificationKey.TOP_N_SORT_ORDER);
    if (
      !topNEnabled ||
      !limit ||
      !measureId ||
      !sortOrder ||
      !this.#isAttributeLastInTheBundle(attribute) ||
      attribute.requestAllDimension ||
      this.#isChartColorAttribute()
    ) {
      // If requestAllDimension is active, TopN will be disabled
      return null;
    }

    const referencedMeasure = this.#dataView
      .getAllAttributesIgnoringUnusedBundle()
      .find((measure) => measure.id === measureId);
    if (!referencedMeasure) {
      return null;
    }

    return {
      sortOrder: TopNSortOrder.i18n(`top-n-information.TopBottom.${sortOrder}`),
      limit: String(limit),
      measureAggregation: MeasureAggregation.i18n(referencedMeasure.measureAggregation!),
      measureName: referencedMeasure.printName,
    };
  }

  #isAttributeLastInTheBundle(attribute: WorkbookAttributeDto) {
    const attributeBundle = this.#dataView.getAttributes(this.#bundleName);
    return attributeBundle[attributeBundle.length - 1] === attribute;
  }

  #isChartColorAttribute() {
    return this.#bundleName === 'COLOR_CLASSIFICATION' && getViewType(this.#dataView) === 'CHART';
  }

  modifyAttribute(
    attributeId: ActiveAttributeId,
    key: AttributeModificationKey,
    value: string | null,
  ): Promise<unknown>;
  modifyAttribute(
    attributeId: ActiveAttributeId,
    modification: [key: AttributeModificationKey, value: string | null][],
  ): Promise<unknown>;
  modifyAttribute(
    attributeId: ActiveAttributeId,
    modification: AttributeModificationKey | [AttributeModificationKey, string | null][],
    value: string | null = null,
  ) {
    if (Array.isArray(modification)) {
      return this.dataView.modifyAttribute(attributeId, modification);
    } else {
      return this.dataView.modifyAttribute(attributeId, modification, value);
    }
  }
  modifyAttributeAndRules(
    attributeId: ActiveAttributeId,
    modifications: [AttributeModificationKey, string][],
    rules: AttributeHighlightingRule[],
  ) {
    return this.dataView.modifyAttributeAndRules(attributeId, modifications, rules);
  }

  #getAttributeTileErrorProvider(attribute: WorkbookAttributeDto, alertText: string) {
    return getAttributeTileErrorProvider(this.dataView.dataViewId, attribute, alertText);
  }

  /**
   * Overrides the drop controller accepts method to add additional checks.
   */
  override accepts(attribute: WorkbookAttributeDto, enforceAttributesNumberLimit = true) {
    const attributesInBundles = Object.values(this.dataView.getAllAttributeBundles()).flat();
    if (!attribute.visibleInDataBrowser && !attributesInBundles.includes(attribute)) {
      return { accepts: false };
    }
    return this.#validator.dataViewControllerAccepts(
      this.getBundleDefinition(),
      attribute,
      true,
      enforceAttributesNumberLimit,
      this.#dataView.visualizationKey !== 'WEBPAGE.WEBPAGE',
    );
  }

  /**
   * Get the bundleName this controller filtering the attributes to.
   * For use in subclasses only!
   */
  get bundleName(): AttributeBundleName {
    return this.#bundleName;
  }

  /**
   * Get the dataView this controller is backed by.
   * For use in subclasses only! (But that's currently not the case.)
   *
   * @return The DataView this controller works on
   */
  get dataView() {
    return this.#dataView;
  }

  override get dropPanelPlaceholderMessage() {
    return this.#dropPanelPlaceholderMessage;
  }

  override get viewCoverAddActionMessage() {
    return this.#viewCoverAddActionMessage;
  }

  /** Gets message and display options for the bundle. */
  getMessage(detailed?: boolean): {
    message?: string;
    isOptional: boolean;
    hideDropArea?: boolean;
    action?: () => void;
  } {
    if (
      !areRequiredAttributesFulfilled(
        getStore().getState(),
        getViewById(getStore().getState(), this.#dataView.viewId),
        getDataViewById(getStore().getState(), this.#dataView.dataViewId),
        this.#bundleName,
      )
    ) {
      return {
        message: this.#requiredAttributesMissingMessage,
        isOptional: false,
      };
    }

    if (this.#defaultMessage) {
      return {
        message: this.#defaultMessage,
        isOptional: true,
      };
    }
    return {
      message: '',
      isOptional: false,
    };
  }

  override canAcceptMoreAttributes() {
    // if there is invalid attribute in the dataView, then we disable plus button
    return (
      super.canAcceptMoreAttributes() &&
      !this.#dataView.getAllAttributes().some((attribute) => attribute.status !== 'OK')
    );
  }

  getMenuItems(attributeId: ActiveAttributeId) {
    const attribute = getAttributeById(getStore().getState(), attributeId);
    // if an attribute is missing, all actions besides remove should be disabled
    const isMissing = () => attribute.status === 'MISSING';
    const menuItems: MenuContentElement[] = [];

    if (attribute.hierarchyType) {
      const hierarchyActions = createHierarchyControlMenuActions(this.#dataView.dataViewId, attribute, {
        disabledFn: isMissing,
      });
      menuItems.push(hierarchyActions);
    }

    if (
      isWorkbookFeatureAvailable(getStore().getState(), 'workbook-condition-management') &&
      analysisContextAccepts(attribute)
    ) {
      menuItems.push({
        label: i18n('attribute.action.addToAnalysisContext'),
        execute: () => addAttributeToAnalysisContextAction(requireNonNullable(attribute.baseId), attributeId),
      });
    }

    if (menuItems.length) {
      menuItems.push(MENU_ITEM_SEPARATOR);
    }

    if (this.#compatibleBundleProvider) {
      menuItems.push(this.#getMoveToBundleMenuItem(attribute));
    }

    // Menu items to move attributes up, down, to top and to bottom of the bundle
    menuItems.push(createMoveAttributeInBundleSubMenu(this, attributeId));

    if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
      menuItems.push(MENU_ITEM_SEPARATOR, {
        label: i18n('attribute.action.edit'),
        execute: () =>
          editActiveAttribute(attributeId, this.#dataView.dataViewId, {
            readOnly: this.#isJasperReportView(),
            customInfoMessage: this.#customInfoMessage,
          }),
        disabled: isMissing(),
      });

      if (isModified(attribute)) {
        menuItems.push({
          label: i18n('attribute.action.reset'),
          execute: () => this.dataView.resetAttribute(attributeId),
          disabled: isMissing(),
        });
      }
    }
    return menuItems;
  }

  /**
   * Creates an entry with submenu for moving the given attribute to compatible bundles.
   *
   * @param attribute - The attribute to find compatible bundles for
   * @return The submenu
   */
  #getMoveToBundleMenuItem(attribute: WorkbookAttributeDto) {
    // ! – because we only call this method if `#compatibleBundleProvider` is present
    const compatibleBundles = this.#compatibleBundleProvider!.get(attribute).filter(
      ({ name }) => name !== this.#bundleName,
    );

    return {
      label: i18n('attribute.action.move'),
      subMenu: () =>
        compatibleBundles.map(({ name, title }) => ({
          label: title,
          execute: () => this.#dataView.addAttribute(attribute.id, name),
        })),
      disabled: compatibleBundles.length === 0,
    };
  }

  #createHierarchyControlMenuActions(hierarchy: WorkbookAttributeDto): MenuItemGroup {
    return createHierarchyControlMenuActions(this.#dataView.dataViewId, hierarchy);
  }

  set locked(locked) {
    this.#locked = locked;
  }

  get locked() {
    return this.#locked;
  }
}

function createSrsDiffersAlert() {
  return h('.alerts', h('.d-alert.d-alert-xs.d-alert-warning', i18n('attribute.srsDiffers')));
}

function createHierarchyContextControlMenuButton(
  attribute: WorkbookAttributeDto,
  hierarchyControlActionsProvider: (_: WorkbookAttributeDto) => MenuItemGroup,
  isHierarchyControlledByMapScale: boolean,
) {
  const menuIcon = getHierarchyContextMenuIcon(
    isHierarchyControlledByMapScale,
    attribute.usesLocalHierarchyLevel ?? false,
  );
  const menuButton = createIconButton(menuIcon, i18n('hierarchy.control.menu.button'), {
    variant: 'borderless',
    size: 'xxs',
    styleClass: 'hierarchyContextControlButton',
  });
  setupMenuButton(menuButton, () => [hierarchyControlActionsProvider(attribute)]);
  return menuButton;
}

/**
 * Temporary - new function to make it work with new d-attribute-tile component. We don't remove the
 * old function as it's still used in some places.
 */
function createTopNLimitedContentForAttributeTile(topNInformation: TopNInformation) {
  return h('div.ellipsis', i18n('attribute.limitedTopNResult', topNInformation as unknown as Record<string, string>));
}

function getHierarchyContextMenuIcon(isHierarchyControlledByMapScale: boolean, usesLocalHierarchyLevel: boolean) {
  if (isHierarchyControlledByMapScale) {
    return icon(scaleDependentSvg);
  }
  return usesLocalHierarchyLevel ? icon(indicatorLocalContextSvg) : icon(indicatorAnalysisContextSvg);
}

export function getAttributeTileErrorProvider(
  dataViewId: DataViewId,
  attribute: WorkbookAttributeDto,
  alertText: string,
) {
  return (title: string) => {
    return h('d-alert.d-alert-small.d-alert-error', [
      h('p.title', title),
      h('p.d-alert--text', alertText),
      isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management') ?
        h(
          'button.d-anchor',
          {
            onclick: () => editActiveAttribute(attribute.id as ActiveAttributeId, dataViewId),
          },
          i18n('error.anchor.incompatibleAggregation'),
        )
      : null,
    ]);
  };
}
